/**
 * IM:TSPLINE and IM:TSPLINEX, the tension spline of EAV.RDP.INTERP (see
 * interpMethods.ts, which holds the method table). Our own code, written from
 * the method's mathematical definition and matched to LSEG's goldens
 * (docs/lseg-golden/outputs/interp_dense*_out.json).
 *
 * Between two nodes the curve solves f'''' = sigma^2 f'' (an exponential
 * tension spline): with z_i = f''(x_i), h = x_{i+1} - x_i, A = x_{i+1} - x, B = x - x_i,
 *   f(x) = (z_i sinh(sigma A) + z_{i+1} sinh(sigma B)) / (sigma^2 sinh(sigma h))
 *          + (y_i - z_i / sigma^2) A / h + (y_{i+1} - z_{i+1} / sigma^2) B / h.
 * The tension sigma is 0.756 for every curve and every interval (found on each
 * piece of d1, d2b, d4 and p3/p4 separately: 0.756 to 1e-12).
 */

import { crExp, crExpm1 } from "./correctlyRounded";

// LSEG: Adfin (22038) AdInterp (its error text where no non-zero Y is left, goldens d7, d8, and past a repeated last X, d24)
const BAD_INPUTS = "ERROR 9346 - Bad inputs in the first column.";

// LSEG: Adfin (22038) AdInterp IM:TSPLINE (the tension, from the dense goldens)
const SIGMA = 0.756;
const SIGMA2 = SIGMA * SIGMA;

/** sinh and cosh as (e^a -/+ e^-a) / 2, with exp correctly rounded: the forms that give LSEG's last digits. */
const sinh = (a: number) => (crExp(a) - crExp(-a)) / 2;
const cosh = (a: number) => (crExp(a) + crExp(-a)) / 2;

/**
 * tanh for a >= 0 from expm1 (correctly rounded), in three ranges:
 * 1 - 2 / (expm1(2a) + 2) above log(3)/2, t / (t + 2) with t = expm1(2a)
 * above log(5/3)/2, and -t / (t + 2) with t = expm1(-2a) below. This is the
 * form that gives the goldens' last digits; V8's Math.tanh differs in the
 * last bit at 0.756 (sigma h for h = 1), which changes the 15th decimal of d1.
 */
function tanh(a: number): number {
  if (a > 0.5493061443340548) return 1 - 2 / (crExpm1(2 * a) + 2); // log(3) / 2
  if (a > 0.25541281188299536) {
    const t = crExpm1(2 * a); // log(5/3) / 2
    return t / (t + 2);
  }
  const t = crExpm1(-2 * a);
  return -t / (t + 2);
}

/**
 * The second derivatives z at the nodes: natural ends (z = 0 at the first
 * and last node) and, at each inner node i, the continuity of f':
 *   E(h_{i-2}) z_{i-1} + (D(h_{i-1}) + D(h_i)) z_i + E(h_i) z_{i+1} = sigma^2 (S_i - S_{i-1}),
 * with E(h) = 1/h - sigma / sinh(sigma h), D(h) = sigma (1 / tanh(sigma h)) - 1/h
 * and S_i the secant slopes. The coefficient of z_{i-1} is LSEG's: E of the
 * interval before the previous one, not E(h_{i-1}) (so f' jumps at the inner
 * nodes after the second; d1 at x = 4: -1.653 from the left, -1.308 from the
 * right). The goldens of d1, d2b and d4 force it (solving each node's
 * equation for that coefficient gives E(h_{i-2}) to 12 digits). Solved by
 * elimination forward (multiplier, pivot) and substitution back.
 */
// LSEG: Adfin (22038) AdInterp IM:TSPLINE (the system, its index shift and its arithmetic, from the goldens d1, d2b, d4, p3, p4)
function secondDerivatives(xs: number[], ys: number[]): number[] {
  const n = xs.length;
  const h: number[] = [];
  const s: number[] = [];
  const e: number[] = [];
  const d: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    h.push(xs[i + 1] - xs[i]);
    s.push((ys[i + 1] - ys[i]) / h[i]);
    e.push(1 / h[i] - SIGMA / sinh(SIGMA * h[i]));
    d.push(SIGMA * (1 / tanh(SIGMA * h[i])) - 1 / h[i]);
  }
  const z = new Array<number>(n).fill(0);
  const pivot = new Array<number>(n).fill(0);
  const rhs = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const diag = d[i - 1] + d[i];
    const r = SIGMA2 * (s[i] - s[i - 1]);
    if (i === 1) {
      pivot[i] = diag;
      rhs[i] = r;
    } else {
      const l = e[i - 2] / pivot[i - 1];
      pivot[i] = diag - l * e[i - 1];
      rhs[i] = r - l * rhs[i - 1];
    }
  }
  for (let i = n - 2; i >= 1; i--) z[i] = (rhs[i] - e[i] * z[i + 1]) / pivot[i];
  return z;
}

/**
 * The tension spline at x. A node gives its own Y. Outside the range it is
 * flat (TSPLINE) or the line of the end piece's slope there (TSPLINEX),
 *   f'(end) = (z_{i+1} cosh(sigma B) - z_i cosh(sigma A)) / (sigma sinh(sigma h)) - (y_i - z_i/sigma^2) (1/h) + (y_{i+1} - z_{i+1}/sigma^2) (1/h),
 * written so (the reciprocal of h multiplied, not divided): the form that
 * gives LSEG's last digit at d4 13 (the others miss it by 1).
 * An interval so long that sinh(sigma h) overflows (sigma h > 709.78, h > 938:
 * a year and more of date serials) has its sinh ratios as exponentials,
 * sinh(sigma u) / sinh(sigma h) = e^(sigma (u - h)) to double precision, and
 * cosh likewise; LSEG gives numbers there (golden dates, X 45000..49383, whose
 * last interval is 1826 long), not an overflow. Away from the interval's ends
 * that term is below 1e-100 and the piece is its linear part; near the ends
 * it counts, and the goldens there are exact too (datesend: 3 to 23 from
 * either end of the 1826-long interval).
 * A repeated X (golden d9) makes every z 0: LIN's broken line (the later of
 * duplicate points starts the next segment), extended by the end segments.
 * Past a repeated last X (golden d24, X 1,2,10,10) TSPLINE is flat at the
 * last Y, and TSPLINEX, whose end segment has length 0, gives ERROR 9346.
 * A Y of 0 after the first point counts as missing (goldens d6-d8, d15-d17):
 * the piece that would start or end on it reaches over it to the nearest
 * non-zero Y on that side, with the z of the full system (the zeros
 * included). A piece that starts at the first point keeps it whatever its Y
 * (d2b; d7 on (x_1, x_2)); the search left from a 0 needs a non-zero Y (d15
 * finds the first point's). Finding none gives ERROR 9346: past the last
 * non-zero Y (d8, also above the range, even for TSPLINE) or after a leading
 * run of zeros (d7 on (x_2, x_3)). The node itself gives its 0. With a
 * repeated X the reach is along LIN's line (golden d25, X 1,2,4,4,7,10, Y
 * 1,2,0,4,5,6: (2,4) runs from Y 2 to the later 4's Y 4; x = 4 gives 0).
 */
// LSEG: Adfin (22038) AdInterp IM:TSPLINE / IM:TSPLINEX (behaviour and arithmetic from the goldens d1, d2b, d4, d9, d24, d25, p3, p4)
function tensionSpline(x: number, xs: number[], ys: number[], extend: boolean): number | { error: string } {
  const node = xs.indexOf(x);
  if (node >= 0) return ys[node];
  const n = xs.length;
  const distinct = xs.every((v, k) => k === 0 || v !== xs[k - 1]);
  const z = distinct ? secondDerivatives(xs, ys) : new Array<number>(n).fill(0);
  let i = 0;
  while (i < n - 2 && xs[i + 1] <= x) i++;
  let [lo, hi] = [i, i + 1];
  if (lo > 0 && ys[lo] === 0) {
    while (lo >= 0 && ys[lo] === 0) lo--;
    if (lo < 0) return { error: BAD_INPUTS };
  }
  while (hi < n && ys[hi] === 0) hi++;
  if (hi === n) return { error: BAD_INPUTS };
  const h = xs[hi] - xs[lo];
  const [y0, y1, z0, z1] = [ys[lo], ys[hi], z[lo], z[hi]];
  const [w0, w1] = [z0 / SIGMA2, z1 / SIGMA2];
  const sh = sinh(SIGMA * h);
  if (x < xs[0] || x > xs[n - 1]) {
    const left = x < xs[0];
    if (!extend) return left ? ys[0] : ys[n - 1];
    if (h === 0) return { error: BAD_INPUTS };
    const b = left ? 0 : h;
    const a = h - b;
    const inv = 1 / h;
    const hyperbolic = Number.isFinite(sh)
      ? (z1 * cosh(SIGMA * b) - z0 * cosh(SIGMA * a)) / (SIGMA * sh)
      : (z1 * crExp(SIGMA * b - SIGMA * h) - z0 * crExp(SIGMA * a - SIGMA * h)) / SIGMA;
    const slope = hyperbolic - (y0 - w0) * inv + (y1 - w1) * inv;
    const [xe, ye] = left ? [xs[0], ys[0]] : [xs[n - 1], ys[n - 1]];
    return ye + slope * (x - xe);
  }
  const a = xs[hi] - x;
  const b = x - xs[lo];
  const hyperbolic = Number.isFinite(sh)
    ? (z0 * sinh(SIGMA * a) + z1 * sinh(SIGMA * b)) / (SIGMA2 * sh)
    : (z0 * crExp(SIGMA * a - SIGMA * h) + z1 * crExp(SIGMA * b - SIGMA * h)) / SIGMA2;
  return hyperbolic + ((y0 - w0) * a) / h + ((y1 - w1) * b) / h;
}

// LSEG: Adfin (22038) AdInterp IM:TSPLINE (flat outside the range)
export const tspline = (x: number, xs: number[], ys: number[]) => tensionSpline(x, xs, ys, false);
// LSEG: Adfin (22038) AdInterp IM:TSPLINEX (extended outside the range)
export const tsplinex = (x: number, xs: number[], ys: number[]) => tensionSpline(x, xs, ys, true);

/**
 * The curves on which TSPLINE/TSPLINEX are proven exact: some Y not 0, the
 * first X not repeated (no golden; a repeated inner X is d9 and d25, a
 * repeated last X d24), and on distinct X every Y of 0 in a leading or
 * trailing run (d2b, d7, d8).
 * On distinct X a Y of 0 between two non-zero Y follows the same rule and the
 * goldens confirm it (d6, d15, d16, d17), but 3 of their points miss LSEG's
 * value by 1 or 2 ulps (d6 at 2.5 and 4.5, d16 at 4.25: 1 in the 15th
 * decimal), on reaching and ordinary pieces alike, with no solver or
 * evaluation order found that mends them (HANDOFF.md). With a repeated X
 * every z is 0, the pieces are lines and that arithmetic is not involved:
 * d25 (an inner 0 at a repeated X) is exact.
 */
// LSEG: Adfin (22038) AdInterp IM:TSPLINE / IM:TSPLINEX (the inputs the goldens prove; see docs/lseg-golden/HANDOFF.md)
export function tsplineMatched(xs: number[], ys: number[]): boolean {
  const first = ys.findIndex((y) => y !== 0);
  const last = ys.length - 1 - [...ys].reverse().findIndex((y) => y !== 0);
  if (first < 0) return false; // every Y 0: no golden
  if (xs[0] === xs[1]) return false; // a repeated first X: no golden
  const distinct = xs.every((v, k) => k === 0 || v !== xs[k - 1]);
  const innerZero = ys.slice(first, last + 1).includes(0);
  return !(distinct && innerZero);
}
